import { AgGridModule } from '@ag-grid-community/angular';
import { RowClickedEvent } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AccessManagementUserCollectionRecord } from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import {
  CdwConfirmationDialogComponent,
  CdwDialogModule,
  ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { DrawerContent } from '@cdw-ae/common-ui-components-drawer-content';
import {
  CdwColDef,
  CdwSearchGridComponent,
  CdwSearchGridModule,
} from '@cdw-ae/common-ui-components-search-grid';
import { CdwSelectModule } from '@cdw-ae/common-ui-components-select';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { take } from 'rxjs';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { UserCollectionsFilterSettings } from '../../../../../../common/services/route-config/configs/filter-bar-config.config';
import { EmptyStateOverlayComponent } from '../../../../../../components/empty-state-overlay/empty-state-overlay.component';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';
import { UserCollectionsDataService } from './user-collections-data.service';

@Component({
  selector: 'userCollections',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    CdwSearchGridModule,
    CdwSelectModule,
    CdwDialogModule,
    FilterBarTextMatchersModule,
    EmptyStateOverlayComponent,
  ],
  templateUrl: './user-collections.component.html',
  styleUrl: './user-collections.component.scss',
})
export class UserCollectionsComponent implements OnInit {
  public isLoading = false;
  @ViewChild('userCollectionGrid')
  searchGridComponent!: CdwSearchGridComponent<AccessManagementUserCollectionRecord>;
  public drawerContent: DrawerContent[] = [];
  public drawerContentTitle = '';
  public filterBarSettings!: FilterBarField[];
  public userCollectionsSearchComponentId = 'userCollectionsComponent';
  public readonly searchFieldMask: string[] = ['user.name', 'name', 'description'];
  public filterBySelectedColumnName = 'id';

  @Output() updateDrawerContent =
    new EventEmitter<AccessManagementUserCollectionRecord>();

  public columnDefs: CdwColDef[] = [
    {
      field: 'name',
      headerName: 'User Collection',
      width: 300,
      sort: 'asc',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 600,
    },
    {
      cellRenderer: 'deleteItemIconComponent',
      headerName: 'Delete',
      flex: 1,
    },
  ];

  public itemLabel = 'userCollection';

  // Theme colors for search grid
  public customTextColor: string = TailwindColors.gray300;
  public firstColumnTextColor: string = TailwindColors.tertiaryBlueLink;

  // Show empty state overlay for testing
  showEmptyState = false;

  // HTML content for empty state overlay
  public userCollectionsEmptyStateHtml = this.getEmptyStateHtml();

  constructor(
    public userCollectionsDataService: UserCollectionsDataService,
    private accessManagementAuthService: AccessManagementFrontendAuthService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.filterBarSettings = UserCollectionsFilterSettings.filter(
      (filterBarField: FilterBarField): boolean =>
        this.searchFieldMask.includes(filterBarField.name)
    );
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    await this.accessManagementAuthService.setUserAuthContext();
  }

  public onRowClicked(event: RowClickedEvent): void {
    this.emitUpdateDrawerContent(event);
  }

  deleteUserCollectionClicked(event: {
    rowId: number | string;
    gridComponentId?: string;
  }) {
    const id = (event as unknown as { rowId: number }).rowId;
    const customerId =
      this.accessManagementAuthService.authContext?.sqlCustomerId ?? null;
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      return;
    }

    // Check if user collection can be deleted before showing confirmation dialog
    this.userCollectionsDataService
      .canDeleteUserCollection(id)
      .pipe(take(1))
      .subscribe((canDeleteResponse) => {
        if (!canDeleteResponse.canDelete) {
          // Show error immediately without confirmation dialog
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data:
              canDeleteResponse.message ||
              'Error deleting user collection, it is assigned to a designation',
            duration: 6000,
          });
          return;
        }

        // User collection can be deleted, show confirmation dialog
        const deleteDialogRef = this.dialog.open(
          CdwConfirmationDialogComponent,
          {
            data: {
              title: 'Confirmation',
              message: 'Are you sure you want to delete this user collection?',
              type: 'warning',
              layout: 'warning-box',
              confirmText: 'Delete',
              cancelText: 'Cancel',
              confirmButtonColor: 'warn',
              hard: true,
            },
            width: '540px',
            maxWidth: '100%',
            disableClose: true,
            hasBackdrop: true,
          }
        );

        deleteDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(async (result: ConfirmationDialogResult | undefined) => {
            if (result?.confirmed && id) {
              (await this.userCollectionsDataService.deleteUserCollection(id))
                .pipe(take(1))
                .subscribe((response) => {
                  if (response.success) {
                    this.snackbar.open(
                      'User collection deleted successfully.',
                      'OK',
                      { duration: 6000 }
                    );
                    this.refreshSearchGrid();
                  } else {
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                      data: 'Error deleting user collection, it is assigned to a designation',
                    });
                  }
                });
            }
          });
      });
  }

  emitUpdateDrawerContent(
    event: RowClickedEvent<AccessManagementUserCollectionRecord>
  ) {
    this.updateDrawerContent.emit(event.data);
  }

  refreshSearchGrid() {
    this.searchGridComponent.refreshGrid();
  }

  onAddNewUserCollection(): void {
    this.router.navigate([
      'administration/access-management/user-collection/edit/new',
    ]);
  }

  getEmptyStateHtml(): string {
    return `No user collections have been set up. Start by <a href="/administration/access-management/user-collection/edit/new" class="empty-state-link">adding a new user collection</a>.`;
  }

  onDataLoaded(): void {
    this.isLoading = false;
  }
}
